/**
 * CompleteReservationModal — Wizard 2 étapes pour compléter une réservation table.
 *
 * Étape 1 (data) : Saisie optionnelle du montant payé + plats consommés depuis la carte.
 * Étape 2 (method) : Choix de la méthode de validation parmi 3 options :
 *   - classique : marque la résa completed sans crédit fidélité (clients sans app)
 *   - qr : scan du wallet QR du client → CF crédite atomiquement
 *   - nfc : pending tx + tap puce NFC du resto par le client → CF crédite atomiquement
 *
 * Les données saisies (amount + dishes) sont propagées dans toutes les méthodes pour
 * que la résa porte ces métadonnées en analytics, quel que soit le chemin choisi.
 */

import React, { useEffect, useMemo, useState } from 'react';
import type { MenuItem, DishSnapshot } from '../../shared/types';
import { firestoreService } from '../../shared/services/firestoreService';
import { SmartphoneIcon, WaveIcon, CheckIcon, WarningIcon } from './icons/UiIcons';
import { getBackendService } from '../../shared/services/apiConfig';
import AmountAndDishesForm from './shared/AmountAndDishesForm';
import ClientQrScanner from './ClientQrScanner';
import RestaurateurPendingPOS from './RestaurateurPendingPOS';

interface ReservationLite {
    id: string;
    customerName: string;
    userId?: string;
    /** Montant (centimes) et plats choisis par le client à la réservation, s'il y en a. */
    amount?: number;
    dishes?: DishSnapshot[];
    /** Offre flash sur place : la ligne porte l'id de l'offre, pas celui du plat de la carte. */
    flashPromoId?: string;
    flashMenuItemId?: string;
    offerQuantity?: number;
}

interface CompleteReservationModalProps {
    reservation: ReservationLite;
    restaurantId: string;
    onClose: () => void;
    onCompleted: (result: {
        method: 'classic' | 'qr' | 'nfc';
        pointsEarned?: number;
        creditApplied?: boolean;
        alreadyCredited?: boolean;
        newTierName?: string;
    }) => void;
}

type Step = 'data' | 'method' | 'classic-confirm' | 'qr' | 'nfc' | 'success' | 'error';

const CompleteReservationModal: React.FC<CompleteReservationModalProps> = ({
    reservation,
    restaurantId,
    onClose,
    onCompleted,
}) => {
    const [step, setStep] = useState<Step>('data');
    const [menu, setMenu] = useState<MenuItem[]>([]);
    const [menuLoaded, setMenuLoaded] = useState(false);
    // Plats choisis par le client à la réservation : seuls les plats encore à la carte sont
    // repris, AU PRIX DE LA CARTE (le prix écrit par le client n'est jamais utilisé),
    // quantités plafonnées comme côté serveur (4 par plat, 12 lignes).
    const prefilledDishes = useMemo<DishSnapshot[]>(() => {
        if (!menuLoaded || !Array.isArray(reservation.dishes)) return [];
        const byId = new Map<string, MenuItem>(menu.filter((m) => m && m.id && m.available !== false).map((m) => [m.id, m]));
        const out: DishSnapshot[] = [];
        for (const d of reservation.dishes.slice(0, 12)) {
            // Ligne de l'offre : au prix de l'offre et en quantité réservée (vérifiés par les règles).
            if (reservation.flashPromoId && d?.menuItemId === reservation.flashPromoId) {
                out.push({
                    menuItemId: reservation.flashMenuItemId ?? d.menuItemId,
                    name: d.name,
                    quantity: reservation.offerQuantity ?? 1,
                    unitPrice: d.unitPrice,
                });
                continue;
            }
            const m = d?.menuItemId ? byId.get(d.menuItemId) : undefined;
            if (!m) continue;
            const quantity = Math.min(4, Math.max(1, Math.round(Number(d.quantity) || 1)));
            out.push({ menuItemId: m.id, name: m.name, quantity, unitPrice: Math.round((Number(m.price) || 0) * 100) });
        }
        return out;
    }, [menu, menuLoaded, reservation.dishes, reservation.flashPromoId, reservation.flashMenuItemId, reservation.offerQuantity]);
    const isFlash = !!reservation.flashPromoId;
    // Offre flash : le prix des offres × quantité, modifiable : l'addition réelle le remplace.
    const offersTotal = isFlash ? prefilledDishes.reduce((sum, d) => sum + d.quantity * d.unitPrice, 0) : 0;
    const prefilledAmount = typeof reservation.amount === 'number' && reservation.amount > 0
        ? reservation.amount
        : offersTotal > 0 ? offersTotal : undefined;
    const [data, setData] = useState<{ amountCents: number | null; dishes: DishSnapshot[] }>({
        amountCents: prefilledAmount ?? null,
        dishes: [],
    });
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    // Une résa créée via l'app cliente est déjà liée à un compte → pas besoin de
    // proposer NFC/QR pour identifier le client. Direct vers la complétion.
    const isLinkedToAccount = !!reservation.userId;
    const nextStepAfterData: Step = isLinkedToAccount ? 'classic-confirm' : 'method';

    // Charger la carte du restaurant pour le DishPicker
    useEffect(() => {
        let isMounted = true;
        (async () => {
            try {
                const backend = await getBackendService();
                const r = await backend.getRestaurant(restaurantId);
                if (r && isMounted) setMenu((r as any).menu || []);
            } catch (e) {
                console.warn('Could not load menu for picker', e);
            } finally {
                if (isMounted) setMenuLoaded(true);
            }
        })();
        return () => { isMounted = false; };
    }, [restaurantId]);

    const handleClassicConfirm = async () => {
        setSubmitting(true);
        setErrorMsg('');
        try {
            const res = await firestoreService.completeTableReservationClassic({
                reservationId: reservation.id,
                ...(data.amountCents !== null ? { amountCents: data.amountCents } : {}),
                ...(data.dishes.length > 0 ? { dishes: data.dishes } : {}),
            });
            setStep('success');
            onCompleted({
                method: 'classic',
                pointsEarned: res.pointsEarned,
                creditApplied: res.creditApplied,
                alreadyCredited: res.alreadyCredited,
                newTierName: res.tierChanged ? res.newTierName : undefined,
            });
        } catch (err: any) {
            console.error('Classic complete failed', err);
            setErrorMsg(err?.message ?? 'Erreur lors de la complétion');
            setStep('error');
        } finally {
            setSubmitting(false);
        }
    };

    const stepHeader = (
        <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 mb-3">
            <span className={step === 'data' ? 'text-indigo-600' : ''}>1. Détails</span>
            <span className="text-gray-300">→</span>
            <span className={['method', 'classic-confirm', 'qr', 'nfc'].includes(step) ? 'text-indigo-600' : ''}>2. Validation</span>
        </div>
    );

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="complete-reservation-title"
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="p-5 border-b border-gray-100">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <h2 id="complete-reservation-title" className="text-lg font-bold text-gray-900">
                                Compléter la réservation
                            </h2>
                            <p className="text-sm text-gray-500 mt-0.5">{reservation.customerName}</p>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Fermer"
                            className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
                        >
                            ×
                        </button>
                    </div>
                </div>

                <div className="p-5">
                    {stepHeader}

                    {step === 'data' && (
                        <div className="space-y-4">
                            {isLinkedToAccount ? (
                                <p className="text-xs text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 rounded-lg p-3 inline-flex items-start gap-2">
                                    <CheckIcon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                    <span>Cette réservation est liée au compte de <strong>{reservation.customerName}</strong>. Le crédit fidélité sera appliqué automatiquement — pas besoin de scanner.</span>
                                </p>
                            ) : (
                                <p className="text-xs text-gray-500">
                                    Optionnel — capturez le montant et les plats consommés pour vos analytics.
                                </p>
                            )}
                            {prefilledDishes.length > 0 && (
                                <p className="text-xs text-indigo-700 bg-indigo-50 rounded-lg p-2">
                                    {isFlash
                                        ? "Offres réservées par le client. Saisissez l'addition réelle : sans montant, le prix des offres sert de minimum."
                                        : 'Le client a choisi ses plats à la réservation : vérifiez, ajustez si besoin, puis validez.'}
                                </p>
                            )}
                            {/* Remonté quand la carte arrive : les plats préremplis prennent les prix de la carte. */}
                            <AmountAndDishesForm
                                key={menuLoaded ? 'menu' : 'loading'}
                                menu={menu}
                                initialAmount={prefilledAmount}
                                initialDishes={prefilledDishes}
                                onChange={setData}
                            />
                            <div className="flex gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => { setData({ amountCents: null, dishes: [] }); setStep(nextStepAfterData); }}
                                    className="flex-1 py-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg"
                                >
                                    Passer
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setStep(nextStepAfterData)}
                                    className="flex-1 py-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg"
                                >
                                    Continuer →
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 'method' && (
                        <div className="space-y-3">
                            <p className="text-xs text-gray-500 mb-2">
                                Comment valider la fidélité de ce client ?
                            </p>
                            <button
                                type="button"
                                onClick={() => setStep('qr')}
                                className="w-full p-4 text-left border border-gray-200 rounded-xl hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="w-10 h-10 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center flex-shrink-0" aria-hidden="true">
                                        <SmartphoneIcon className="w-5 h-5" />
                                    </span>
                                    <div>
                                        <div className="font-bold text-gray-900 dark:text-gray-100 text-sm">Scanner le QR du client</div>
                                        <div className="text-xs text-gray-500 mt-0.5">Le client ouvre son wallet, vous scannez. Crédit immédiat.</div>
                                    </div>
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => setStep('nfc')}
                                className="w-full p-4 text-left border border-gray-200 rounded-xl hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="w-10 h-10 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center flex-shrink-0" aria-hidden="true">
                                        <WaveIcon className="w-5 h-5" />
                                    </span>
                                    <div>
                                        <div className="font-bold text-gray-900 dark:text-gray-100 text-sm">NFC — client tape la puce</div>
                                        <div className="text-xs text-gray-500 mt-0.5">Le client approche son téléphone de la puce du restaurant. Crédit asynchrone.</div>
                                    </div>
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => setStep('classic-confirm')}
                                className="w-full p-4 text-left border border-gray-200 rounded-xl hover:border-gray-400 hover:bg-gray-50 transition-colors"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                                        <CheckIcon className="w-5 h-5" />
                                    </span>
                                    <div>
                                        <div className="font-bold text-gray-900 dark:text-gray-100 text-sm">Terminée — sans scan</div>
                                        <div className="text-xs text-gray-500 mt-0.5">
                                            {reservation.userId
                                                ? 'La résa est liée à un compte : crédit fidélité automatique.'
                                                : 'Réservation manuelle (walk-in) : marque terminée sans crédit.'}
                                        </div>
                                    </div>
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => setStep('data')}
                                className="w-full pt-2 text-xs text-gray-400 hover:text-gray-700"
                            >
                                ← Retour aux détails
                            </button>
                        </div>
                    )}

                    {step === 'classic-confirm' && (
                        <div className="space-y-4">
                            {reservation.userId ? (
                                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                                    <p className="text-sm text-emerald-900 dark:text-emerald-200 font-semibold mb-1 inline-flex items-center gap-1.5"><CheckIcon className="w-4 h-4" /> Crédit fidélité automatique</p>
                                    <p className="text-xs text-emerald-800">
                                        Le compte de {reservation.customerName} sera crédité (visite + points/tampons + paliers).
                                        Pas besoin de scanner — la résa est déjà liée.
                                    </p>
                                </div>
                            ) : (
                                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                                    <p className="text-sm text-amber-900 dark:text-amber-200 font-semibold mb-1 inline-flex items-center gap-1.5"><WarningIcon className="w-4 h-4" /> Sans crédit fidélité</p>
                                    <p className="text-xs text-amber-800">
                                        Cette résa n'est pas liée à un compte client (walk-in / saisie manuelle).
                                        Elle sera marquée terminée avec vos détails analytics, sans crédit de points.
                                    </p>
                                </div>
                            )}
                            {(data.amountCents !== null || data.dishes.length > 0) && (
                                <div className="text-xs text-gray-600 space-y-0.5">
                                    {data.amountCents !== null && (
                                        <div>Montant : <strong>{(data.amountCents / 100).toFixed(2)}€</strong></div>
                                    )}
                                    {data.dishes.length > 0 && (
                                        <div>{data.dishes.length} plat{data.dishes.length > 1 ? 's' : ''} sélectionné{data.dishes.length > 1 ? 's' : ''}</div>
                                    )}
                                </div>
                            )}
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setStep(isLinkedToAccount ? 'data' : 'method')}
                                    disabled={submitting}
                                    className="flex-1 py-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
                                >
                                    Retour
                                </button>
                                <button
                                    type="button"
                                    onClick={handleClassicConfirm}
                                    disabled={submitting}
                                    className="flex-1 py-2 text-sm font-bold text-white bg-gray-900 hover:bg-gray-800 rounded-lg disabled:opacity-50"
                                >
                                    {submitting ? 'Validation…' : 'Confirmer terminée'}
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 'qr' && (
                        <div className="-m-5">
                            {/* ClientQrScanner est un overlay fullscreen — on le laisse prendre la place */}
                            <ClientQrScanner
                                linkedReservationId={reservation.id}
                                amountCents={data.amountCents ?? undefined}
                                dishes={data.dishes.length > 0 ? data.dishes : undefined}
                                onSuccess={(r) => onCompleted({ method: 'qr', pointsEarned: r.pointsEarned })}
                                onClose={onClose}
                            />
                        </div>
                    )}

                    {step === 'nfc' && (
                        <RestaurateurPendingPOS
                            restaurantId={restaurantId}
                            linkedReservationId={reservation.id}
                            prefilledAmountCents={data.amountCents ?? undefined}
                            prefilledItems={data.dishes.length > 0 ? data.dishes : undefined}
                            // Skip le formulaire interne quand le montant a déjà été saisi à l'étape Détails.
                            autoSubmit={typeof data.amountCents === 'number' && data.amountCents > 0}
                            onSuccess={(s) => onCompleted({ method: 'nfc', pointsEarned: s.pointsAwarded })}
                        />
                    )}

                    {step === 'success' && (
                        <div className="text-center py-6">
                            <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-emerald-100 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center" aria-hidden="true">
                                <CheckIcon className="w-8 h-8" />
                            </div>
                            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">Réservation terminée</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{reservation.customerName}</p>
                            <button
                                type="button"
                                onClick={onClose}
                                className="mt-5 px-6 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800"
                            >
                                Fermer
                            </button>
                        </div>
                    )}

                    {step === 'error' && (
                        <div className="space-y-3">
                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-sm text-red-700 font-semibold">Erreur</p>
                                <p className="text-xs text-red-600 mt-0.5">{errorMsg}</p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setStep('method')}
                                    className="flex-1 py-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg"
                                >
                                    Retour
                                </button>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="flex-1 py-2 text-sm font-bold text-white bg-gray-900 hover:bg-gray-800 rounded-lg"
                                >
                                    Fermer
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CompleteReservationModal;
